import { z } from 'zod';

export const crmCustomerRecordContextViewId = 'crm.customerRecordContext.v1' as const;
export const crmCustomerRecordDeliveredAreaSchema = z.enum([
  'overview',
  'membership',
  'learning',
  'training',
  'documents',
  'sync',
]);

const customerHeaderSchema = z
  .object({
    avatarUrl: z.url().nullable(),
    avatarVisibility: z.enum(['visible', 'unavailable']),
    customerId: z.uuid(),
    displayName: z.string().min(1),
    freshnessState: z.enum(['fresh', 'stale', 'unknown']),
  })
  .superRefine((header, context) => {
    if (
      (header.avatarVisibility === 'visible' && header.avatarUrl === null) ||
      (header.avatarVisibility === 'unavailable' && header.avatarUrl !== null)
    ) {
      context.addIssue({ code: 'custom', path: ['avatarUrl'], message: 'Invalid avatar state' });
    }
  });

export const crmCustomerRecordContextResponseSchema = z.object({
  data: z
    .object({
      allowedAreas: z.array(crmCustomerRecordDeliveredAreaSchema),
      authorizationFingerprint: z.string().min(32),
      capabilities: z.array(
        z.object({
          actions: z.tuple([z.literal('read')]).readonly(),
          area: crmCustomerRecordDeliveredAreaSchema,
        })
      ),
      customer: customerHeaderSchema.nullable(),
      state: z.enum(['ready', 'restricted']),
      viewId: z.literal(crmCustomerRecordContextViewId),
    })
    .superRefine((data, context) => {
      const ready = data.state === 'ready';
      if (
        (ready && (data.customer === null || data.allowedAreas.length === 0)) ||
        (!ready && (data.customer !== null || data.allowedAreas.length !== 0))
      ) {
        context.addIssue({ code: 'custom', path: ['state'], message: 'Invalid context state' });
      }
      if (
        data.capabilities.length !== data.allowedAreas.length ||
        data.capabilities.some((capability, index) => capability.area !== data.allowedAreas[index])
      ) {
        context.addIssue({
          code: 'custom',
          path: ['capabilities'],
          message: 'Capabilities do not match allowed areas',
        });
      }
    }),
});

export type CrmCustomerRecordDeliveredArea = z.infer<typeof crmCustomerRecordDeliveredAreaSchema>;
export type CrmCustomerRecordContextResponse = z.infer<
  typeof crmCustomerRecordContextResponseSchema
>;
